/**
 * Composants réutilisables de l'interface.
 * Aucun montant n'est écrit ici : les valeurs viennent toutes de l'appelant.
 */

import { dateFr, euros, eurosCentimes, type NiveauEvaluation } from '@scc/core';
import { ecritures } from './api.js';
import { h, richeTexte, vider } from './dom.js';

export type TonKpi = 'neutral' | 'progress' | 'warning' | 'alert';
export type TonInsight = 'positive' | 'warning' | 'negative' | 'info';

export function kpi(
  ton: TonKpi,
  valeur: string,
  unite: string | null,
  libelle: string,
  delta?: { sens: 'up' | 'down' | 'neutre'; texte: string },
): HTMLElement {
  return h(
    'div',
    { classe: `kpi ${ton}` },
    h('div', { classe: 'kpi-value' }, valeur, unite ? h('span', { classe: 'unit' }, ` ${unite}`) : null),
    h('div', { classe: 'kpi-label' }, libelle),
    delta
      ? h(
          'span',
          {
            classe: `kpi-delta ${delta.sens === 'up' ? 'delta-up' : delta.sens === 'down' ? 'delta-down' : 'delta-neutral'}`,
          },
          delta.texte,
        )
      : null,
  );
}

export function insight(ton: TonInsight, icone: string, texte: string): HTMLElement {
  return h(
    'div',
    { classe: `insight ${ton}` },
    h('span', { classe: 'insight-icon', 'aria-hidden': 'true' }, icone),
    h('p', {}, richeTexte(texte)),
  );
}

export function enteteSection(numero: number, titre: string, sousTitre: string): HTMLElement {
  return h(
    'div',
    { classe: 'section-head' },
    h('div', { classe: 'section-number', 'aria-hidden': 'true' }, String(numero)),
    h('div', {}, h('h2', {}, titre), h('p', { classe: 'section-sub' }, sousTitre)),
  );
}

export function regleAccent(libelle: string): HTMLElement {
  return h(
    'div',
    { classe: 'accent-rule' },
    h('div', { classe: 'rule', 'aria-hidden': 'true' }),
    h('span', { classe: 'label' }, libelle),
    h('div', { classe: 'rule', 'aria-hidden': 'true' }),
  );
}

export function noteLecture(texte: string): HTMLElement {
  return h('p', { classe: 'note-lecture' }, richeTexte(texte));
}

export function badge(niveau: NiveauEvaluation | 'neutre', texte: string): HTMLElement {
  const classe =
    niveau === 'favorable'
      ? 'b-green'
      : niveau === 'vigilance'
        ? 'b-amber'
        : niveau === 'alerte'
          ? 'b-danger'
          : 'b-rose';
  return h('span', { classe: `badge ${classe}` }, texte);
}

/** Barre de progression accessible : le pourcentage est aussi donné en texte. */
export function barre(part: number, teinte: string, texte: string): HTMLElement {
  const pct = Math.max(0, Math.min(100, part * 100));
  return h(
    'div',
    { classe: 'bar-wrap' },
    h(
      'div',
      {
        classe: 'bar-bg',
        role: 'progressbar',
        'aria-valuenow': Math.round(pct),
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'aria-label': texte,
      },
      h('div', { classe: `bar-fill ${teinte}`, '--part': `${Math.max(1, pct).toFixed(1)}%` }),
    ),
    h('span', { classe: 'bar-val' }, texte),
  );
}

/**
 * Cellule cliquable ouvrant le détail des écritures qui composent un total.
 * Un élu doit pouvoir remonter d'un total à la ligne bancaire en deux clics.
 */
export function montantTracable(
  poste: string,
  libelle: string,
  montant: number,
  sens?: 'recette' | 'depense',
): HTMLElement {
  return h(
    'button',
    {
      classe: 'tracable',
      type: 'button',
      'aria-label': `${libelle} : ${euros(montant)}. Afficher les écritures.`,
      surClick: () => void ouvrirDetail(poste, libelle, sens),
    },
    euros(montant),
  );
}

/** Origine du classement d'une écriture, telle que le détail l'affiche. */
const SOURCES_CLASSEMENT: Readonly<Record<string, string>> = {
  compte: 'Compte Zoho',
  regle: 'Règle',
  jev: 'Jev',
  arbitrage: 'Arbitrage',
};

let dialogue: HTMLDialogElement | null = null;

async function ouvrirDetail(poste: string, libelle: string, sens?: 'recette' | 'depense'): Promise<void> {
  if (!dialogue) {
    dialogue = h('dialog', { classe: 'detail' });
    document.body.appendChild(dialogue);
  }
  const d = dialogue;
  vider(d);
  const corps = h('div', { classe: 'corps' }, h('p', {}, 'Chargement des écritures…'));
  d.appendChild(
    h(
      'div',
      { classe: 'entete' },
      h('h3', {}, libelle),
      h('button', { classe: 'fermer', type: 'button', surClick: () => { d.close(); } }, 'Fermer'),
    ),
  );
  d.appendChild(corps);
  d.showModal();

  try {
    const detail = await ecritures(poste, sens);
    // Colonne présente seulement quand le module Classement renseigne la source.
    const avecSource = detail.ecritures.some((e) => e.source !== undefined);
    vider(corps);
    if (detail.ecritures.length === 0) {
      corps.appendChild(h('p', {}, 'Aucune écriture ne compose ce total.'));
      return;
    }
    corps.appendChild(
      h(
        'p',
        { classe: 'note-lecture' },
        `${detail.nb} écriture${detail.nb > 1 ? 's' : ''}, total ${eurosCentimes(detail.total)}.`,
      ),
    );
    corps.appendChild(
      h(
        'table',
        { classe: 'comp-table' },
        h('caption', { classe: 'invisible' }, `Écritures composant : ${libelle}`),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Date'),
            h('th', { scope: 'col' }, 'Libellé'),
            h('th', { scope: 'col' }, 'Compte'),
            h('th', { scope: 'col' }, 'Exercice'),
            avecSource ? h('th', { scope: 'col' }, 'Classement') : null,
            h('th', { scope: 'col' }, 'Montant'),
          ),
        ),
        h(
          'tbody',
          {},
          ...detail.ecritures.map((e) =>
            h(
              'tr',
              {},
              h('td', {}, dateFr(e.date)),
              h('td', {}, e.libelle || '—'),
              h('td', {}, e.compteLibelle || '—'),
              h('td', {}, e.exerciceAffectation === null ? 'non renseigné' : String(e.exerciceAffectation)),
              avecSource ? h('td', {}, SOURCES_CLASSEMENT[e.source ?? ''] ?? '—') : null,
              h('td', { classe: 'num' }, eurosCentimes(e.montant)),
            ),
          ),
        ),
      ),
    );
  } catch {
    vider(corps);
    corps.appendChild(h('p', {}, 'Le détail des écritures n’a pas pu être chargé.'));
  }
}

/** Barre d'export d'un tableau. */
export function barreExport(cle: string, libelle: string): HTMLElement {
  return h(
    'div',
    { classe: 'barre-actions' },
    h('span', { classe: 'titre' }, 'Exporter'),
    h('a', { classe: 'btn-action', href: `/api/exports/${cle}.csv`, download: '' }, 'CSV'),
    h('a', { classe: 'btn-action', href: `/api/exports/${cle}.xlsx`, download: '' }, 'XLSX'),
    h('span', { classe: 'invisible' }, libelle),
  );
}

/** Tableau générique avec en-têtes correctement portés. */
export function tableau(
  classe: string,
  legende: string,
  colonnes: readonly string[],
  lignes: readonly (readonly (string | Node)[])[],
): HTMLElement {
  return h(
    'table',
    { classe },
    h('caption', { classe: 'invisible' }, legende),
    h('thead', {}, h('tr', {}, ...colonnes.map((c) => h('th', { scope: 'col' }, c)))),
    h(
      'tbody',
      {},
      ...lignes.map((l) =>
        h(
          'tr',
          {},
          ...l.map((cellule, i) =>
            i === 0
              ? h('th', { scope: 'row' }, cellule)
              : h('td', { classe: typeof cellule === 'string' ? 'num' : '' }, cellule),
          ),
        ),
      ),
    ),
  );
}
